'use client';

import {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import {
  ChatMessage,
  MessageType,
  ChatContextType,
  APIData,
} from '../types/chat.types';
import { streamChat, generateSessionId } from '../services/chatApiService';

const ChatContext = createContext<ChatContextType | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState(() => generateSessionId());
  const [history, setHistory] = useState<
    Array<{ role: 'user' | 'system'; content: string }>
  >([]);
  const [apiData, setApiData] = useState<APIData>({
    properties: [],
    pois: [],
    transportRoutes: [],
    transportStops: [],
    dloiPins: [],
    commuteRoutes: [],
  });
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasAISearchCompleted, setHasAISearchCompleted] = useState(false);
  const [isPropertyDetail, setIsPropertyDetail] = useState(false);

  const appendMessage = useCallback((msg: ChatMessage) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const updateLastAIMessage = useCallback((chunk: string, done = false) => {
    setMessages((prev) => {
      if (!prev.length) return prev;

      const last = prev[prev.length - 1];
      if (last.type !== MessageType.AI) return prev;

      return [
        ...prev.slice(0, -1),
        {
          ...last,
          content: last.content + chunk,
          status: done ? 'sent' : 'streaming',
        },
      ];
    });
  }, []);

  const sendMessage = useCallback(
    async (message: string, messageType: MessageType = MessageType.USER) => {
      if (!message.trim()) return;

      // Add message with specified type
      appendMessage({
        id: crypto.randomUUID(),
        type: messageType,
        content: message,
        status: 'sent',
      });

      // Add AI message placeholder only for USER messages
      if (messageType === MessageType.USER) {
        appendMessage({
          id: crypto.randomUUID(),
          type: MessageType.AI,
          content: 'Loading...',
          status: 'streaming',
        });
      }

      // NOTE: isStreaming is NOT set to true here anymore.
      // It is only set to true when 'loading_event' with needs_search=true arrives.

      try {
        let responseText = '';
        let propertyMetadataReceived = false;
        const currentApiData: APIData = {
          properties: [],
          pois: [],
          transportRoutes: [],
          transportStops: [],
          dloiPins: [],
          commuteRoutes: [],
        };

        // Stream the chat API
        for await (const event of streamChat(message, sessionId, history)) {
          switch (event.type) {
            case 'loading_event':
              // Only enable streaming indicator when a real search is needed
              if (event.needs_search === true) {
                setIsStreaming(true);
              }
              if (event.is_property_detail === true) {
                setIsPropertyDetail(true);
              }
              break;

            case 'progress':
              // Append progress messages (stack them)
              appendMessage({
                id: crypto.randomUUID(),
                type: MessageType.PROGRESS,
                content: event.content,
                status: 'sent',
              });
              break;

            case 'progress_clear':
              // Remove all progress messages
              setMessages((prev) =>
                prev.filter((m) => m.type !== MessageType.PROGRESS)
              );
              break;

            case 'stream':
              // Append text chunk to AI message
              responseText += event.content || '';

              setMessages((prev) => {
                const lastMsg = prev[prev.length - 1];
                const newContent = event.content || '';

                // If no AI message exists or last message isn't AI, create one
                if (!lastMsg || lastMsg.type !== MessageType.AI) {
                  const filtered = prev.filter(
                    (m) => m.type !== MessageType.QUICK_SUGGESTION
                  );
                  return [
                    ...filtered,
                    {
                      id: crypto.randomUUID(),
                      type: MessageType.AI,
                      content: newContent,
                      status: 'streaming',
                    },
                  ];
                }

                // Update existing AI message - clear placeholder or append content
                const isPlaceholder = lastMsg.content === 'Loading...';
                return [
                  ...prev.slice(0, -1),
                  {
                    ...lastMsg,
                    content: isPlaceholder
                      ? newContent
                      : lastMsg.content + newContent,
                    status: 'streaming',
                  },
                ];
              });
              break;

            case 'property_metadata':
              propertyMetadataReceived = true;
              currentApiData.properties = event.properties || [];
              break;

            case 'poi_pins':
              currentApiData.pois = [
                ...(currentApiData.pois || []),
                ...(event.pins || []),
              ];
              break;

            case 'transport_routes':
              currentApiData.transportRoutes = event.routes || [];
              break;

            case 'transport_stops':
              currentApiData.transportStops = [
                ...(currentApiData.transportStops || []),
                ...(event.stops || []),
              ];
              break;

            case 'dloi_pins':
              currentApiData.dloiPins = event.pins || [];
              break;

            case 'commute_routes':
              currentApiData.commuteRoutes = [
                ...(currentApiData.commuteRoutes || []),
                ...(event.routes || []),
              ];
              break;

            case 'extracted_filters':
              currentApiData.metaFilters = event.filters ?? null;
              setApiData((prev) => ({
                ...prev,
                metaFilters: event.filters ?? null,
              }));
              break;

            case 'clear_all':
              // setHasAISearchCompleted(false);
              // Preserve metaFilters: extracted_filters arrives before clear_all in the
              // stream, so wiping the whole object would cancel the filter animation.
              setApiData((prev) => ({
                properties: [],
                pois: [],
                transportRoutes: [],
                transportStops: [],
                dloiPins: [],
                commuteRoutes: [],
                metaFilters: prev.metaFilters,
              }));
              break;

            case 'clear_poi':
              setApiData((prev) => ({ ...prev, pois: [] }));
              break;

            case 'clear_transit':
              setApiData((prev) => ({
                ...prev,
                transportRoutes: [],
                transportStops: [],
              }));
              break;

            case 'clear_listings':
              setApiData((prev) => ({ ...prev, properties: [] }));
              break;

            case 'done':
              // Only update property-related state if the backend sent a property_metadata event.
              // If it never arrived, preserve whatever was shown from the previous query.
              if (propertyMetadataReceived) {
                setHasAISearchCompleted(true);
              }

              // Step 1: POIs and DLOI pins first
              setApiData((prev) => ({
                ...prev,
                pois: currentApiData.pois,
                dloiPins: currentApiData.dloiPins,
              }));

              // Step 2: Transport stops and routes
              await new Promise((resolve) => setTimeout(resolve, 0));
              setApiData((prev) => ({
                ...prev,
                transportStops: currentApiData.transportStops,
                transportRoutes: currentApiData.transportRoutes,
              }));

              // Step 3: Commute routes
              await new Promise((resolve) => setTimeout(resolve, 0));
              setApiData((prev) => ({
                ...prev,
                commuteRoutes: currentApiData.commuteRoutes,
              }));

              // Step 4: Properties — only overwrite if property_metadata was received
              await new Promise((resolve) => setTimeout(resolve, 0));
              if (propertyMetadataReceived) {
                setApiData((prev) => ({
                  ...prev,
                  properties: currentApiData.properties,
                  // metaFilters: currentApiData.metaFilters,
                }));
              }

              // Step 5: Finalize AI message stream status
              await new Promise((resolve) => setTimeout(resolve, 0));
              setMessages((prev) => {
                const lastMsg = prev[prev.length - 1];
                if (!lastMsg || lastMsg.type !== MessageType.AI) {
                  return [
                    ...prev,
                    {
                      id: crypto.randomUUID(),
                      type: MessageType.AI,
                      content: responseText,
                      status: 'sent',
                    },
                  ];
                }
                return [...prev.slice(0, -1), { ...lastMsg, status: 'sent' }];
              });

              setHistory((prev) => [
                ...prev,
                { role: 'user', content: message },
                { role: 'system', content: responseText },
              ]);
              break;

            case 'error':
              setIsStreaming(false);

              // Remove the "Loading..." placeholder message
              setMessages((prev) =>
                prev.filter(
                  (m) =>
                    !(m.type === MessageType.AI && m.content === 'Loading...')
                )
              );

              // Map HTTP status codes to user-friendly messages
              let errorMsg = event.content || 'Unknown error from server';
              const { status } = event as { status?: number; content?: string };

              if (status === 500 || status === 502 || status === 503) {
                errorMsg =
                  'The server is not reachable currently. Sorry for the inconvenience.';
              } else if (status === 403) {
                errorMsg = 'Please login to continue the conversation.';
              }

              appendMessage({
                id: crypto.randomUUID(),
                type: MessageType.AI,
                content: errorMsg,
                status: 'error',
              });
              console.error(
                '[Chat] Backend error:',
                errorMsg,
                'Status:',
                status
              );
              break;

            default:
              console.warn(
                '[Chat] Unknown event type:',
                (event as Record<string, unknown>).type
              );
          }
        }
      } catch (error) {
        console.error('[Chat] Stream error:', error);
        updateLastAIMessage('', true);
        appendMessage({
          id: crypto.randomUUID(),
          type: MessageType.AI,
          content: `Connection error: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          status: 'error',
        });
      } finally {
        setIsStreaming(false);
        setIsPropertyDetail(false);
      }
    },
    [sessionId, history, appendMessage, updateLastAIMessage]
  );

  const resetMessages = useCallback(() => {
    setMessages([]);
    setHistory([]);
    setSessionId(generateSessionId());
    setHasAISearchCompleted(false);
    setIsPropertyDetail(false);
    setApiData({
      properties: [],
      pois: [],
      transportRoutes: [],
      transportStops: [],
      dloiPins: [],
      commuteRoutes: [],
      metaFilters: null,
    });
  }, []);

  const value = useMemo(
    () => ({
      // UI controls
      isOpen,
      openChat: () => setIsOpen(true),
      closeChat: () => setIsOpen(false),
      toggleChat: () => setIsOpen((prev) => !prev),

      // Messages
      messages,
      isStreaming,
      appendMessage,
      updateLastAIMessage,
      resetMessages,

      // Session & History
      sessionId,
      history,

      // API Data
      apiData,

      // Search state
      hasAISearchCompleted,
      isPropertyDetail,

      // Actions
      sendMessage,
    }),
    [
      isOpen,
      messages,
      isStreaming,
      appendMessage,
      updateLastAIMessage,
      resetMessages,
      sessionId,
      history,
      apiData,
      hasAISearchCompleted,
      isPropertyDetail,
      sendMessage,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChat must be used inside <ChatProvider>');
  }
  return ctx;
}
